/**
 * AiProviderFilter.js
 *
 * AI-guided provider filter dialog. Launched by the chatbot — no text input.
 * Refinement happens through the chatbot (voice or text).
 * Criteria updates arrive via Redux state.app.aiLabelFilter.criteria.
 * Save is triggered by chatbot (saveTrigger) or the Save button.
 *
 * Props:
 *   existingNamespaces: TagNamespace[] with providers_drop_down=true
 *   clientId:           number
 *   aiLabelFilter:      state.app.aiLabelFilter slice
 *   onClose:            () => void
 *   onSaved:            (result) => void
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import axiosInstance from 'utils/axiosUtil';
import { SpecialtyUtil } from 'doctivity-shared/utils';
import { setAiLabelFilter, closeAiLabelFilter } from 'store/actions/appActions';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FilterListIcon,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon   from '@mui/icons-material/CheckCircle';
import HubIcon            from '@mui/icons-material/Hub';
import { ToggleButton }   from '@mui/material';
import { Icon } from '@mui/material';
import CloseIcon         from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FilterListIcon2   from '@mui/icons-material/FilterList';
import LabelIcon         from '@mui/icons-material/Label';

const PAGE_SIZE = 25;

const EMPTY_CRITERIA = {
  counties:           [],
  states:             [],
  cities:             [],
  firstNames:         [],
  lastNames:          [],
  npis:               [],
  specialtyKeywords:  [],
  affiliatedOnly:     false,
  excludeLastNames:   [],
  excludeCities:      [],
  excludeProviderIds: [],
  summary:            '',
};

// ── Save mode label ───────────────────────────────────────────────────────────
function SaveModeHeader({ aiLabelFilter }) {
  const { mode, namespaceName, tagName, namespaceId, tagId, existingNamespaces } = aiLabelFilter;
  
  let label = '';
  if (mode === 'new_namespace') {
    label = `New label group "${namespaceName}" → "${tagName}"`;
  } else if (mode === 'new_tag') {
    const ns = existingNamespaces?.find((n) => n.id === namespaceId);
    label = `New label "${tagName}" in group "${ns?.name || namespaceId}"`;
  } else if (mode === 'existing_tag') {
    const ns = existingNamespaces?.find((n) => n.id === namespaceId);
    const tag = ns?.tags?.find((t) => t.id === tagId);
    label = `Adding to label "${tag?.name || tagId}" in group "${ns?.name || namespaceId}"`;
  }

  if (!label) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      <LabelIcon fontSize='small' color='primary' />
      <Typography variant='body2' color='primary.dark' fontWeight={500}>
        {label}
      </Typography>
    </Box>
  );
}

// ── Criteria chips ─────────────────────────────────────────────────────────────
function CriteriaChips({ criteria, onRemove }) {
  if (!criteria) return null;

  const includeChips = [
    ...( criteria.firstNames        || []).map((n)   => ({ label: `First: ${n}`,       key: `fn-${n}`,    color: 'primary',   onDelete: () => onRemove('firstNames', n)       })),
    ...( criteria.lastNames         || []).map((n)   => ({ label: `Last: ${n}`,        key: `ln-${n}`,    color: 'primary',   onDelete: () => onRemove('lastNames', n)        })),
    ...( criteria.npis              || []).map((npi) => ({ label: `NPI: ${npi}`,       key: `npi-${npi}`, color: 'primary',   onDelete: () => onRemove('npis', npi)           })),
    ...( criteria.cities            || []).map((c)   => ({ label: `City: ${c}`,        key: `city-${c}`,  color: 'primary',   onDelete: () => onRemove('cities', c)           })),
    ...( criteria.counties          || []).map((c)   => ({ label: `County: ${c}`,      key: `co-${c}`,    color: 'primary',   onDelete: () => onRemove('counties', c)         })),
    ...( criteria.states            || []).map((s)   => ({ label: `State: ${s}`,       key: `st-${s}`,    color: 'primary',   onDelete: () => onRemove('states', s)           })),
    ...( criteria.specialtyKeywords || []).map((kw)  => ({ label: kw,                  key: `kw-${kw}`,   color: 'secondary', onDelete: () => onRemove('specialtyKeywords', kw) })),
  ];

  const excludeChips = [
    ...( criteria.excludeLastNames  || []).map((n)   => ({ label: `Excl. Last: ${n}`,  key: `exln-${n}`,  onDelete: () => onRemove('excludeLastNames', n)  })),
    ...( criteria.excludeCities     || []).map((c)   => ({ label: `Excl. City: ${c}`,  key: `exct-${c}`,  onDelete: () => onRemove('excludeCities', c)     })),
  ];

  const affiliatedChip = criteria.affiliatedOnly
    ? [{ label: 'Affiliated only', key: 'affiliated', color: 'success', onDelete: () => onRemove('affiliatedOnly', false) }]
    : [];

  const allChips = [...affiliatedChip, ...includeChips, ...excludeChips];
  if (allChips.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center', mb: 1 }}>
      <FilterListIcon2 sx={{ fontSize: 16, color: 'text.secondary', mr: 0.5 }} />
      {allChips.map((chip) => (
        <Chip
          key={chip.key}
          label={chip.label}
          size='small'
          color={chip.color || 'error'}
          variant='outlined'
          onDelete={chip.onDelete}
        />
      ))}
    </Box>
  );
}

// ── Main dialog ────────────────────────────────────────────────────────────────
function AiProviderFilter({ aiLabelFilter, existingNamespaces, clientId, dispatch }) {
  const { open, mode, criteria, saveTrigger } = aiLabelFilter;

  const [providers,    setProviders]    = useState([]);
  const [totalCount,   setTotalCount]   = useState(0);
  const [page,         setPage]         = useState(0);
  const [isFetching,   setIsFetching]   = useState(false);
  const [fetchError,   setFetchError]   = useState(null);
  const [isSaving,     setIsSaving]     = useState(false);
  const [saveError,    setSaveError]    = useState(null);
  const [savedMsg,     setSavedMsg]     = useState(null);

  // Track the criteria we last fetched so we know when to reset page
  const lastCriteriaRef = useRef(null);

  // ── Fetch providers when criteria or page changes ────────────────────────
  const fetchProviders = useCallback(async (activeCriteria, activePage = 0) => {
    if (!activeCriteria) return;
    setIsFetching(true);
    setFetchError(null);
    try {
      const resp = await axiosInstance.post('/AI', {
        type:      'queryProvidersByCriteria',
        criteria:  activeCriteria,
        limit:     PAGE_SIZE,
        offset:    activePage * PAGE_SIZE,
        client_id: clientId,
      });
      setProviders(resp.data?.rows || []);
      setTotalCount(resp.data?.count || 0);
    } catch (err) {
      console.error('[AiProviderFilter] fetchProviders error:', err);
      setFetchError('Failed to load providers. Please try again.');
    } finally {
      setIsFetching(false);
    }
  }, [clientId]);

  // When criteria changes (chatbot refinement), reset page and re-fetch
  useEffect(() => {
    if (!open || !criteria) return;
    const criteriaChanged = JSON.stringify(criteria) !== JSON.stringify(lastCriteriaRef.current);
    if (criteriaChanged) {
      lastCriteriaRef.current = criteria;
      setPage(0);
      fetchProviders(criteria, 0);
    }
  }, [criteria, open, fetchProviders]);

  // When page changes, re-fetch with current criteria
  useEffect(() => {
    if (!open || !criteria) return;
    fetchProviders(criteria, page);
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initial fetch when dialog opens
  useEffect(() => {
    if (open && criteria && !lastCriteriaRef.current) {
      lastCriteriaRef.current = criteria;
      fetchProviders(criteria, 0);
    }
    if (!open) {
      // Reset everything when dialog closes
      setProviders([]);
      setTotalCount(0);
      setPage(0);
      setSavedMsg(null);
      setSaveError(null);
      lastCriteriaRef.current = null;
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    if (!mode || !criteria) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      const resp = await axiosInstance.post('/AI', {
        type:          'saveProviderLabels',
        mode,
        namespaceName: aiLabelFilter.namespaceName,
        tagName:       aiLabelFilter.tagName,
        namespaceId:   aiLabelFilter.namespaceId,
        tagId:         aiLabelFilter.tagId,
        criteria,
        client_id:     clientId,
      });

      if (resp.data?.success) {
        const result = { success: true, count: resp.data.count, tag_id: resp.data.tag_id };
        setSavedMsg(`Saved — ${resp.data.count.toLocaleString()} providers tagged.`);
        dispatch(setAiLabelFilter({ savedResult: result, saveTrigger: false }));
        window.dispatchEvent(new Event('refresh-provider-tags'));
      } else {
        setSaveError(resp.data?.error || 'Save failed.');
        dispatch(setAiLabelFilter({ saveTrigger: false }));
      }
    } catch (err) {
      console.error('[AiProviderFilter] save error:', err);
      setSaveError('Save failed. Please try again.');
      dispatch(setAiLabelFilter({ saveTrigger: false }));
    } finally {
      setIsSaving(false);
    }
  }, [mode, criteria, aiLabelFilter, clientId, dispatch]);

  // Watch saveTrigger from chatbot
  useEffect(() => {
    if (saveTrigger && !isSaving) {
      handleSave();
    }
  }, [saveTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Exclude a provider (appends to criteria.excludeProviderIds) ──────────
  const handleExclude = (providerId) => {
    const next = {
      ...criteria,
      excludeProviderIds: [...(criteria.excludeProviderIds || []), providerId],
    };
    dispatch(setAiLabelFilter({ criteria: next }));
  };

  // Unified chip removal — handles all criteria fields
  const handleRemove = (field, value) => {
    let next;
    if (field === 'affiliatedOnly') {
      next = { ...criteria, affiliatedOnly: false };
    } else if (Array.isArray(criteria[field])) {
      next = { ...criteria, [field]: criteria[field].filter((v) => v !== value) };
    } else {
      next = { ...criteria, [field]: value };
    }
    dispatch(setAiLabelFilter({ criteria: next }));
    // Also fetch directly — don't wait for Redux round-trip
    setPage(0);
    fetchProviders(next, 0);
  };

  const handleToggleAffiliated = () => {
    const next = { ...criteria, affiliatedOnly: !criteria.affiliatedOnly };
    dispatch(setAiLabelFilter({ criteria: next }));
    setPage(0);
    fetchProviders(next, 0);
  };

  const handleClose = () => {
    dispatch(closeAiLabelFilter());
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Dialog
      open={!!open}
      onClose={handleClose}
      maxWidth='xl'
      fullWidth
      PaperProps={{ sx: { height: '90vh', display: 'flex', flexDirection: 'column' } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LabelIcon color='primary' />
            <Typography variant='h6' fontWeight={600}>AI Provider Filter</Typography>
          </Box>
          <IconButton size='small' onClick={handleClose}>
            <CloseIcon fontSize='small' />
          </IconButton>
        </Box>

        <SaveModeHeader aiLabelFilter={{ ...aiLabelFilter, existingNamespaces }} />

        {criteria?.summary && (
          <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
            {criteria.summary}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 0, overflow: 'hidden' }}>

        {/* Chips */}
        <CriteriaChips criteria={criteria} onRemove={handleRemove} />

        {/* Alerts */}
        {fetchError && <Alert severity='error' onClose={() => setFetchError(null)}>{fetchError}</Alert>}
        {saveError  && <Alert severity='error' onClose={() => setSaveError(null)}>{saveError}</Alert>}
        {savedMsg   && (
          <Alert severity='success' icon={<CheckCircleIcon />} onClose={() => setSavedMsg(null)}>
            {savedMsg}
          </Alert>
        )}

        {/* Hint */}
        {!criteria && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
            <Typography color='text.secondary'>
              Use the chatbot to describe the providers you want to include.
            </Typography>
          </Box>
        )}

        {criteria && (<>
          {/* Count + Save */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant='body2' color='text.secondary'>
                {isFetching
                  ? 'Loading…'
                  : `${totalCount.toLocaleString()} provider${totalCount !== 1 ? 's' : ''} match`}
              </Typography>
              <ToggleButton
                value='affiliated'
                size='small'
                selected={!!criteria?.affiliatedOnly}
                onChange={handleToggleAffiliated}
                disabled={isFetching}
                sx={{ px: 1.5, py: 0.25, fontSize: 12, textTransform: 'none' }}
              >
                <HubIcon sx={{ fontSize: 14, mr: 0.5 }} />
                Affiliated only
              </ToggleButton>
            </Box>
            <Button
              variant='contained'
              size='small'
              startIcon={isSaving ? <CircularProgress size={16} /> : <CheckCircleIcon />}
              onClick={handleSave}
              disabled={totalCount === 0 || isFetching || isSaving}
            >
              {isSaving ? 'Saving…' : 'Save Label Group'}
            </Button>
          </Box>

          {/* Table */}
          <TableContainer component={Paper} variant='outlined' sx={{ flex: 1, overflow: 'auto' }}>
            <Table size='small' stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: 14 }} />
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>NPI</strong></TableCell>
                  <TableCell><strong>Specialty</strong></TableCell>
                  <TableCell><strong>Organization</strong></TableCell>
                  <TableCell><strong>City</strong></TableCell>
                  <TableCell><strong>State</strong></TableCell>
                  <TableCell align='right'><strong>Exclude</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isFetching ? (
                  <TableRow>
                    <TableCell colSpan={8} align='center' sx={{ py: 4 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : providers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align='center' sx={{ py: 4, color: 'text.secondary' }}>
                      No providers match the current filters.
                    </TableCell>
                  </TableRow>
                ) : providers.map((p) => (
                  <TableRow key={p.id} hover>
                    <TableCell sx={{ py: 0.75, width: 14 }}>
                      {p.is_affiliated?.is_affiliated ? (
                        <Tooltip title='Affiliated Provider'>
                          <Icon sx={{ fontSize: 14, verticalAlign: 'text-top', color: 'secondary.main' }}>hub</Icon>
                        </Tooltip>
                      ) : null}
                    </TableCell>
                    <TableCell sx={{ py: 0.75 }}>{p.last_name}, {p.first_name}</TableCell>
                    <TableCell sx={{ py: 0.75 }}>{p.npi || '—'}</TableCell>
                    <TableCell sx={{ py: 0.75 }}>
                      {SpecialtyUtil.getNameFromTaxonomy(p.taxonomy) || p.taxonomy?.classification || '—'}
                    </TableCell>
                    <TableCell sx={{ py: 0.75 }}>{p.organizations?.[0]?.name || '—'}</TableCell>
                    <TableCell sx={{ py: 0.75 }}>{p.location?.city || '—'}</TableCell>
                    <TableCell sx={{ py: 0.75 }}>{p.location?.state || '—'}</TableCell>
                    <TableCell sx={{ py: 0.75 }} align='right'>
                      <Tooltip title='Exclude this provider'>
                        <IconButton size='small' onClick={() => handleExclude(p.id)}>
                          <DeleteOutlineIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component='div'
            count={totalCount}
            page={page}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]}
            onPageChange={(_, newPage) => setPage(newPage)}
          />
        </>)}

      </DialogContent>
    </Dialog>
  );
}

AiProviderFilter.propTypes = {
  dispatch:           PropTypes.func.isRequired,
  clientId:           PropTypes.number.isRequired,
  aiLabelFilter:      PropTypes.object.isRequired,
  existingNamespaces: PropTypes.array,
};

AiProviderFilter.defaultProps = {
  existingNamespaces: [],
};

function mapStateToProps(state) {
  return {
    clientId:      state.app.selectedClient,
    aiLabelFilter: state.app.aiLabelFilter || {},
  };
}

const ConnectedAiProviderFilter = connect(mapStateToProps)(AiProviderFilter);
export default ConnectedAiProviderFilter;
export { ConnectedAiProviderFilter as AiProviderFilter };